export function toJinjaStringLiteral(value) {
  const escaped = String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n');
  return `'${escaped}'`;
}

function renderStateExpression(node) {
  const expression = `states(${toJinjaStringLiteral(node.entityId)})`;
  if (!node.fallback) return expression;
  return `(${expression} if ${expression} not in ['unknown', 'unavailable', ''] ` +
    `else ${toJinjaStringLiteral(node.fallback)})`;
}

function renderAttributeExpression(node) {
  const expression =
    `state_attr(${toJinjaStringLiteral(node.entityId)}, ${toJinjaStringLiteral(node.attribute)})`;
  if (!node.fallback) return expression;
  return `(${expression} if ${expression} is not none else ${toJinjaStringLiteral(node.fallback)})`;
}

const DATE_TIME_EXPRESSIONS = {
  time: "now().strftime('%H:%M')",
  hour: 'now().hour',
  minute: 'now().minute',
  second: 'now().second',
  date: "now().strftime('%d-%m-%Y')",
  weekday: "now().strftime('%A')",
  day: 'now().day',
  month: "now().strftime('%B')",
  year: 'now().year',
};

function sourceExpression(source) {
  return source.type === 'entity_attribute'
    ? `state_attr(${toJinjaStringLiteral(source.entityId)}, ${toJinjaStringLiteral(source.attribute)})`
    : `states(${toJinjaStringLiteral(source.entityId)})`;
}

function renderConditionExpression(condition) {
  const source = sourceExpression(condition.source);
  switch (condition.operator) {
    case 'is_on': return condition.source.type === 'entity_state'
      ? `is_state(${toJinjaStringLiteral(condition.source.entityId)}, 'on')`
      : `${source} == 'on'`;
    case 'is_off': return condition.source.type === 'entity_state'
      ? `is_state(${toJinjaStringLiteral(condition.source.entityId)}, 'off')`
      : `${source} == 'off'`;
    case 'not_equals': return `${source} != ${toJinjaStringLiteral(condition.value)}`;
    case 'greater_than': return `${source} | float(0) > ${Number(condition.value)}`;
    case 'greater_or_equal': return `${source} | float(0) >= ${Number(condition.value)}`;
    case 'less_than': return `${source} | float(0) < ${Number(condition.value)}`;
    case 'less_or_equal': return `${source} | float(0) <= ${Number(condition.value)}`;
    case 'contains': return `${toJinjaStringLiteral(condition.value)} in ${source}`;
    default: return `${source} == ${toJinjaStringLiteral(condition.value)}`;
  }
}

function renderDocumentExpression(document) {
  const parts = (document?.nodes ?? []).map(renderNodeExpression);
  return parts.length > 0 ? parts.join(' ~ ') : "''";
}

function renderNodeExpression(node) {
  switch (node.type) {
    case 'text': return toJinjaStringLiteral(node.text);
    case 'entity_state': return renderStateExpression(node);
    case 'entity_attribute': return renderAttributeExpression(node);
    case 'datetime': return DATE_TIME_EXPRESSIONS[node.value] ?? DATE_TIME_EXPRESSIONS.time;
    case 'condition': {
      const whenTrue = renderDocumentExpression(node.whenTrue);
      const whenFalse = renderDocumentExpression(node.whenFalse);
      return `((${whenTrue}) if ${renderConditionExpression(node.condition)} else (${whenFalse}))`;
    }
    default: return "''";
  }
}

function documentIsDynamic(document) {
  return (document?.nodes ?? []).some(node => node.type !== 'text');
}

export function renderTemplateMessage(document) {
  if (!documentIsDynamic(document)) {
    return {
      dynamic: false,
      text: (document?.nodes ?? []).map(node => node.text).join(''),
    };
  }
  return {
    dynamic: true,
    expression: renderDocumentExpression(document),
  };
}

export function renderTemplateDocument(document) {
  const rendered = renderTemplateMessage(document);
  return rendered.dynamic ? `{{ ${rendered.expression} }}` : rendered.text;
}
